// src/lib/useSort.tsx — 全テーブル共通のクリックソート(昇順/降順)
//   ヘッダをクリックで desc→asc→desc をトグル。矢印(▼昇=▲/降=▼・未選択=⇅)を表示。
//   数値は数値順、文字は日本語ロケール順、null は常に末尾。
//   pin 指定行(総合計/病院全体 等)は常に先頭に固定し、残りだけ並べ替える。
//
// 使い方:
//   const s = useSort(rows, { pin: (r) => r.isTotal });   // rows=元配列
//   {s.rows.map(...)}                                       // 並べ替え後を描画
//   <SortTh s={s} sortKey="実績" get={(r) => r.実績} className="text-right ...">実績</SortTh>
//   ※ get は「その列の並べ替えキー」を行から取り出す関数。
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

export type SortDir = "asc" | "desc";
export interface SortApi {
  key: string | null;
  dir: SortDir;
  onSort: (k: string, get: (r: unknown) => unknown) => void;
}

export function useSort<T>(rows: T[], opts?: {
  pin?: (r: T) => boolean;
  defaultSort?: { key: string; dir?: SortDir; get: (r: T) => unknown };
  resetKey?: unknown;
}): { rows: T[] } & SortApi {
  const [key, setKey] = useState<string | null>(opts?.defaultSort?.key ?? null);
  const [dir, setDir] = useState<SortDir>(opts?.defaultSort?.dir ?? "desc");
  const getRef = useRef<((r: unknown) => unknown) | null>(
    opts?.defaultSort ? (r) => opts.defaultSort!.get(r as T) : null,
  );

  // 表示モード等が変わった時、呼び出し側が指定した既定ソートへ戻す。
  useEffect(() => {
    const next = opts?.defaultSort;
    getRef.current = next ? (r) => next.get(r as T) : null;
    setKey(next?.key ?? null);
    setDir(next?.dir ?? "desc");
    // defaultSort の関数は render ごとに生成され得るため、明示的な resetKey のみを監視する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts?.resetKey]);

  const onSort = (k: string, get: (r: unknown) => unknown) => {
    getRef.current = get;
    if (key === k) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setKey(k); setDir("desc"); }
  };

  const sorted = useMemo(() => {
    if (!key || !getRef.current) return rows;
    const get = getRef.current;
    const pinned = opts?.pin ? rows.filter(opts.pin) : [];
    const rest = opts?.pin ? rows.filter((r) => !opts.pin!(r)) : rows.slice();
    rest.sort((a, b) => {
      const av = get(a) as unknown, bv = get(b) as unknown;
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // null は末尾
      if (bv == null) return -1;
      let c: number;
      if (typeof av === "number" && typeof bv === "number") c = av - bv;
      else c = String(av).localeCompare(String(bv), "ja", { numeric: true });
      return dir === "asc" ? c : -c;
    });
    return [...pinned, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, key, dir]);

  return { rows: sorted, key, dir, onSort };
}

// クリック可能な <th>。既存の th の className をそのまま渡せる。title で独自ホバー説明も可。
export function SortTh({ s, sortKey, get, children, className = "", title }: {
  s: SortApi; sortKey: string; get: (r: unknown) => unknown; children: ReactNode; className?: string; title?: string;
}) {
  const active = s.key === sortKey;
  const ind = active ? (s.dir === "asc" ? "▲" : "▼") : "⇅";
  return (
    <th
      className={`cursor-pointer select-none ${className}`}
      onClick={() => s.onSort(sortKey, get)}
      aria-sort={active ? (s.dir === "asc" ? "ascending" : "descending") : "none"}
      title={title ?? "クリックで昇順/降順"}
    >
      <span className="inline-flex items-center gap-0.5">
        {children}
        <span className={`text-[8px] ${active ? "text-slate-600" : "text-slate-300"}`}>{ind}</span>
      </span>
    </th>
  );
}
